import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Board, useUpdateBoard } from '@/entities/board';
import { FormError } from '@/shared/forms';
import { Button, Dialog, MarkdownEditor } from '@/shared/ui';

import styles from './BoardDescription.module.css';

// The Markdown renderer stays out of the main bundle (shared with the card popup chunk).
const Markdown = lazy(() =>
  import('@/shared/ui/Markdown').then((module) => ({ default: module.Markdown })),
);

/** Longer sources are shown collapsed (a visual clamp: the Markdown itself is never cut). */
export const COLLAPSE_OVER = 100;
const MAX_LENGTH = 5000;

interface Props {
  board: Board;
  canEdit: boolean;
}

/**
 * Board description in the board header, rendered as safe Markdown. Clicking the text (or
 * "Add description" when empty) opens the editor. A long description is collapsed with a
 * CSS line clamp, so the rendered Markdown is intact and "Expand" shows all of it.
 */
export function BoardDescription({ board, canEdit }: Props) {
  const { t } = useTranslation();
  const text = board.description ?? '';
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const long = text.length > COLLAPSE_OVER;

  return (
    <div className={styles.root}>
      {text ? (
        <>
          <div
            className={[
              styles.body,
              canEdit && styles.editable,
              long && !expanded && styles.clamped,
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid="board-description"
            title={canEdit ? t('boards.descriptionEditHint') : undefined}
            onClick={(event) => {
              if (!canEdit) return;
              // Links keep working, and selecting text must not open the editor.
              if ((event.target as HTMLElement).closest('a')) return;
              if (window.getSelection()?.toString()) return;
              setEditing(true);
            }}
          >
            <Suspense fallback={null}>
              <Markdown>{text}</Markdown>
            </Suspense>
          </div>
          {long && (
            <button
              type="button"
              className={styles.toggle}
              aria-expanded={expanded}
              onClick={() => {
                setExpanded((value) => !value);
              }}
            >
              {t(expanded ? 'boards.descriptionLess' : 'boards.descriptionMore')}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className={styles.toggle}
              onClick={() => {
                setEditing(true);
              }}
            >
              {t('boards.descriptionEdit')}
            </button>
          )}
        </>
      ) : (
        canEdit && (
          <button
            type="button"
            className={styles.add}
            onClick={() => {
              setEditing(true);
            }}
          >
            + {t('boards.descriptionAdd')}
          </button>
        )
      )}
      {editing && (
        <BoardDescriptionDialog
          board={board}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function BoardDescriptionDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateBoard(board);
  const [value, setValue] = useState(board.description ?? '');

  return (
    <Dialog title={t('boards.descriptionTitle')} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate({ description: value.trim() || null }, { onSuccess: onClose });
        }}
      >
        <MarkdownEditor
          label={t('boards.description')}
          value={value}
          onChange={setValue}
          maxLength={MAX_LENGTH}
          rows={8}
          autoFocus
        />
        <p className={styles.hint}>{t('content.markdownHint')}</p>
        <FormError error={update.error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
